/**
 * StickyContainer 粘性容器组件
 * 
 * 为 Sticky 组件提供上下文
 */
import React, { PureComponent, createContext } from 'react';

type EventHandler = (event: {
  distanceFromTop: number;
  distanceFromBottom: number;
  eventSource: HTMLElement;
}) => void;

export interface StickyContainerContext {
  subscribe: (handler: EventHandler) => void;
  unsubscribe: (handler: EventHandler) => void;
  getParent: () => HTMLElement;
}

export const StickyContext = createContext<StickyContainerContext | null>(null);

export interface StickyContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export default class StickyContainer extends PureComponent<StickyContainerProps> {
  private node: HTMLDivElement | null = null;
  private subscribers: EventHandler[] = [];
  private rafHandle: number | null = null;
  private framePending = false;

  private events = [
    'resize',
    'scroll',
    'touchstart',
    'touchmove',
    'touchend',
    'pageshow',
    'load',
  ];

  subscribe = (handler: EventHandler) => {
    this.subscribers = this.subscribers.concat(handler);
  };

  unsubscribe = (handler: EventHandler) => {
    this.subscribers = this.subscribers.filter((current) => current !== handler);
  };

  getParent = () => this.node!;

  notifySubscribers = (evt: Event) => {
    if (!this.framePending) {
      const currentTarget = evt.currentTarget as HTMLElement;

      this.rafHandle = requestAnimationFrame(() => {
        this.framePending = false;
        if (!this.node) return;

        const { top, bottom } = this.node.getBoundingClientRect();

        this.subscribers.forEach((handler) =>
          handler({
            distanceFromTop: top,
            distanceFromBottom: bottom,
            eventSource: currentTarget === window ? document.body : this.node!,
          })
        );
      });
      this.framePending = true;
    }
  };

  componentDidMount() {
    this.events.forEach((event) =>
      window.addEventListener(event, this.notifySubscribers)
    );
  }

  componentWillUnmount() {
    if (this.rafHandle) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }

    this.events.forEach((event) =>
      window.removeEventListener(event, this.notifySubscribers)
    );
  }

  render() {
    const { children, ...restProps } = this.props;

    return (
      <StickyContext.Provider
        value={{
          subscribe: this.subscribe,
          unsubscribe: this.unsubscribe,
          getParent: this.getParent,
        }}
      >
        <div
          {...restProps}
          ref={(node) => {
            this.node = node;
          }}
          onScroll={this.notifySubscribers as any}
          onTouchStart={this.notifySubscribers as any}
          onTouchMove={this.notifySubscribers as any}
          onTouchEnd={this.notifySubscribers as any}
        >
          {children}
        </div>
      </StickyContext.Provider>
    );
  }
}
